import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../lib/api.js';
import { Badge, Card, EmptyState, ErrorBox, Loading, PageHeader, Stat, STATUS_TONE, Button,
  IconRocket,
} from '../components/ui/index.js';
import { num, pct, relative, STATUS_LABEL } from '../lib/format.js';
import { useAuth } from '../lib/auth.js';

interface DashboardData {
  counts: { blogs: number; publishedPosts: number; activeWidgets: number };
  last30Days: { impressions: number; clicks: number; views: number; ctr: number };
  recentPosts: { id: string; title: string; status: string; viewCount: number; updatedAt: string; url: string }[];
}

interface SummaryData {
  totals: { impressions: number; clicks: number; views: number; ctr: number };
  series: { date: string; impressions: number; clicks: number; views: number }[];
  topPosts: { id: string; title: string; views: number; clicks: number; url: string }[];
  topReferrers: { domain: string; count: number }[];
}

/**
 * กราฟแท่งแบบ inline SVG
 * ไม่ใช้ไลบรารีกราฟเพราะหน้านี้ต้องการแค่แนวโน้ม 30 วัน — การเพิ่ม dependency
 * ~100KB เพื่อกราฟเดียวทำให้หน้าหลักช้าลงโดยไม่ได้อะไรกลับมา
 */
function TrendChart({ series }: { series: SummaryData['series'] }) {
  if (!series.length) return null;
  const max = Math.max(1, ...series.map((d) => Math.max(d.impressions, d.views)));
  const W = 100, H = 34, gap = 0.35;
  const bw = W / series.length;

  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H}`} className="h-32 w-full" preserveAspectRatio="none" role="img"
           aria-label={`แนวโน้ม ${series.length} วันล่าสุด`}>
        {series.map((d, i) => {
          const h = (d.impressions / max) * H;
          const hv = (d.views / max) * H;
          return (
            <g key={d.date}>
              <rect x={i * bw + gap} y={H - h} width={bw - gap * 2} height={h}
                    className="fill-brand-500/25" rx="0.4" />
              <rect x={i * bw + gap} y={H - hv} width={bw - gap * 2} height={hv}
                    className="fill-brand-500" rx="0.4" />
              <title>{`${d.date} — impression ${d.impressions} · view ${d.views} · คลิก ${d.clicks}`}</title>
            </g>
          );
        })}
      </svg>
      <div className="mt-2 flex items-center justify-between text-[11px] text-[var(--ink-3)]">
        <span>{series[0]?.date}</span>
        <span className="flex items-center gap-3">
          <span className="flex items-center gap-1"><i className="inline-block h-2 w-2 rounded-sm bg-[var(--brand)]/25" />impression</span>
          <span className="flex items-center gap-1"><i className="inline-block h-2 w-2 rounded-sm bg-[var(--brand)]" />ยอดอ่าน</span>
        </span>
        <span>{series[series.length - 1]?.date}</span>
      </div>
    </div>
  );
}

export function DashboardPage() {
  const user = useAuth((s) => s.user);

  const dash = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => api.get<{ data: DashboardData }>('/v1/analytics/dashboard').then((r) => r.data),
  });
  const summary = useQuery({
    queryKey: ['summary'],
    queryFn: () => api.get<{ data: SummaryData }>('/v1/analytics/summary').then((r) => r.data),
  });

  if (dash.isLoading) return <Loading />;
  if (dash.error) return <ErrorBox error={dash.error} onRetry={() => void dash.refetch()} />;

  const d = dash.data!;
  const noContent = d.counts.publishedPosts === 0;

  return (
    <>
      <PageHeader
        title={`สวัสดี ${user?.fullName?.split(' ')[0] ?? ''}`}
        description="ภาพรวมบล็อกและ widget ของคุณใน 30 วันล่าสุด"
        actions={<Link to="/blogs"><Button>เขียนบทความใหม่</Button></Link>}
      />

      {noContent ? (
        <EmptyState
          icon={<IconRocket size={22} />} title="เริ่มต้นใช้งาน Blogaris"
          description="สามขั้นตอน: เขียนบทความแรก → สร้าง widget → คัดลอกโค้ดไปวางบนเว็บบริษัท"
          action={<Link to="/blogs"><Button size="lg">เขียนบทความแรก</Button></Link>}
        />
      ) : (
        <>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            <Stat label="ยอดอ่านบทความ" value={num(d.last30Days.views)} sub="30 วันล่าสุด" />
            <Stat label="Impression ของ widget" value={num(d.last30Days.impressions)} sub="จำนวนครั้งที่ widget ถูกเห็น" />
            <Stat label="คลิกจาก widget" value={num(d.last30Days.clicks)} sub="กด More info เข้าอ่านบทความ" />
            <Stat label="CTR" value={pct(d.last30Days.ctr)} sub="คลิก ÷ impression" />
          </div>

          <div className="mt-6 grid gap-6 lg:grid-cols-3">
            <Card className="p-6 lg:col-span-2">
              <div className="mb-5 flex items-center justify-between">
                <div>
                  <h2 className="text-base font-semibold text-[var(--ink)]">แนวโน้ม 30 วัน</h2>
                  <p className="mt-0.5 text-xs text-[var(--ink-3)]">ภาพรวมการเข้าถึงคอนเทนต์ของคุณ</p>
                </div>
                <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-600">Live insights</span>
              </div>
              {summary.isLoading ? <Loading label="" /> : summary.data ? (
                <TrendChart series={summary.data.series} />
              ) : null}
            </Card>

            <Card className="p-5">
              <h2 className="mb-3 text-sm font-semibold text-[var(--ink)]">เว็บที่ฝัง widget มากที่สุด</h2>
              {summary.data?.topReferrers.length ? (
                <ul className="space-y-2">
                  {summary.data.topReferrers.slice(0, 6).map((r) => (
                    <li key={r.domain} className="flex items-center justify-between gap-3 text-sm">
                      <span className="truncate text-[var(--ink-2)]">{r.domain}</span>
                      <span className="tabular-nums text-[var(--ink-3)]">{num(r.count)}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-[var(--ink-3)]">
                  ยังไม่มีข้อมูล — ข้อมูลจะขึ้นเมื่อ widget ถูกฝังบนเว็บจริงและมีคนเข้าชม
                </p>
              )}
            </Card>
          </div>

          <div className="mt-6 grid gap-6 lg:grid-cols-2">
            <Card className="p-5">
              <h2 className="mb-3 text-sm font-semibold text-[var(--ink)]">บทความล่าสุด</h2>
              <ul className="divide-y divide-[var(--line-soft)]">
                {d.recentPosts.map((p) => (
                  <li key={p.id} className="flex items-center gap-3 py-2.5">
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-[var(--ink)]">{p.title}</span>
                      <span className="text-xs text-[var(--ink-3)]">แก้ไข{relative(p.updatedAt)}</span>
                    </span>
                    <Badge tone={STATUS_TONE[p.status] ?? 'gray'}>{STATUS_LABEL[p.status] ?? p.status}</Badge>
                  </li>
                ))}
              </ul>
            </Card>

            <Card className="p-5">
              <h2 className="mb-3 text-sm font-semibold text-[var(--ink)]">บทความที่มีคนอ่านมากที่สุด</h2>
              {summary.data?.topPosts.length ? (
                <ul className="divide-y divide-[var(--line-soft)]">
                  {summary.data.topPosts.slice(0, 5).map((p) => (
                    <li key={p.id} className="flex items-center gap-3 py-2.5">
                      <a href={p.url} target="_blank" rel="noopener"
                         className="min-w-0 flex-1 truncate text-sm text-[var(--ink)] hover:text-[var(--brand)]">
                        {p.title}
                      </a>
                      <span className="shrink-0 text-xs tabular-nums text-[var(--ink-3)]">
                        {num(p.views)} อ่าน · {num(p.clicks)} คลิก
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-[var(--ink-3)]">ยังไม่มีข้อมูลยอดอ่าน</p>
              )}
            </Card>
          </div>
        </>
      )}
    </>
  );
}
